import { Component, OnDestroy, OnInit, viewChild, inject } from '@angular/core';
import {
  ECookieConsent,
  ECookieConsentState,
  ECookieType,
  ICookie,
  ICookieConsentFormValue,
} from '@app/cookie-consent/models/cookie-consent.model';
import {CookieManageService} from '@app/cookie-consent/services/cookie.service';
import {hideCookieConsent, showCookieConsent} from '@app/cookie-consent/state/cookie-policy.actions';
import {selectShowConsentStatus} from '@app/cookie-consent/state/cookie-policy.selector';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {environment} from '@environments/environment';
import {Store} from '@ngrx/store';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {DividerModule} from 'primeng/divider';
import {Subject} from 'rxjs';
import {CookieConsentFormComponent} from '../cookie-consent-form/cookie-consent-form.component';
import {CookieTypeComponent} from '../cookie-type/cookie-type.component';

@Component({
  selector: 'app-cookie-consent',
  imports: [
    CookieConsentFormComponent,
    ButtonModule,
    DividerModule,
    CookieTypeComponent,
    DialogModule,
    TranslateModule,
  ],
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss'],
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  private readonly cookieService = inject(CookieManageService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly oidcSecurityService = inject(OidcSecurityService);

  cookieConsentFormComponent = viewChild(CookieConsentFormComponent);

  header = '';
  isVisible = false;
  shouldShowSettings = false;
  shouldShowAllowSelection = false;
  showState: ECookieConsentState = ECookieConsent.Consent;

  ECookieConsent = ECookieConsent;
  ECookieType = ECookieType;

  strictlyNecessaryCookies: ICookie[] = [];

  unsubscribe$ = new Subject<void>();

  constructor() {
    this.store.select(selectShowConsentStatus).subscribe(consentStatus => {
      this.isVisible = consentStatus;
    });

    this.oidcSecurityService.isAuthenticated$.subscribe(({isAuthenticated}) => {
      if (isAuthenticated) {
        this.isVisible = !this.wasConsentGiven();
        if (this.isVisible) {
          this.store.dispatch(showCookieConsent());
        } else {
          this.store.dispatch(hideCookieConsent());
        }
      } else {
        this.isVisible = false;
        this.store.dispatch(hideCookieConsent());
      }
    });
  }

  ngOnInit(): void {
    this.loadTranslation();
    this.setCookiesState();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private get cookieForm() {
    const comp = this.cookieConsentFormComponent();
    if (!comp) {
      throw new Error('CookieConsentFormComponent not available');
    }
    return comp;
  }

  onAllowNecessaryCookies(): void {
    this.cookieForm.onAllowNecessaryCookies();
  }

  onAllowSelectionCookies(): void {
    this.cookieForm.onAllowSelectionCookies();
  }

  onAllowAllCookies(): void {
    this.cookieForm.onAllowAllCookies();
  }

  onBackToCookieSettings(): void {
    this.showState = ECookieConsent.Consent;
    this.header = this.translateService.instant('CookieConsent.Title');
    this.shouldShowSettings = true;
  }

  onHideCookieConsentModal(): void {
    this.isVisible = false;
    this.store.dispatch(hideCookieConsent());
  }

  onShouldShowAllowSelection(shouldAllowSelection: boolean): void {
    this.shouldShowAllowSelection = shouldAllowSelection;
  }

  onShowMoreInformation(type: ECookieType): void {
    this.showState = type;
    this.header = this.translateService.instant(`CookieConsent.${type}.Title`);
  }

  private setCookiesState(): void {
    this.strictlyNecessaryCookies = this.getStrictlyNecessaryCookies();
  }

  private getStrictlyNecessaryCookies(): ICookie[] {
    return this.cookieService.getStrictlyNecessaryCookies();
  }

  private loadTranslation() {
    const lang = this.translateService.currentLang ?? this.translateService.defaultLang;
    this.translateService.getTranslation(lang).subscribe((data: object) => {
      this.refreshTranslations(data);
    });
  }

  private wasConsentGiven(): boolean {
    if (environment.skipCookieConsent) {
      return true;
    }

    const cookieConsent = this.cookieService.get<ICookieConsentFormValue>(environment.cookie.cookieConsent);
    this.cookieService.cookieConsentState = cookieConsent;
    return !ObjectUtils.isEmpty(cookieConsent);
  }

  private refreshTranslations(data: object) {
    this.header = ObjectUtils.getValueByDotSeparatedKey('CookieConsent.Title', data);
  }
}
